import React, {
  useEffect,
  useId,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: var(--z-modal, 1050);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(12, 24, 17, 0.48);
`;
const Container = styled.div<{ $size: 'sm' | 'md' | 'lg' | 'xl' }>`
  position: relative;
  width: 100%;
  max-width: ${({ $size }) =>
    ({ sm: '400px', md: '500px', lg: '700px', xl: '900px' })[$size]};
  max-height: calc(100dvh - 2rem);
  overflow-y: auto;
  background: var(--surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  &:focus {
    outline: none;
  }
`;
const Header = styled.div`
  padding: 1.5rem 4rem 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
  h2 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    line-height: 1.4;
  }
  p {
    margin: 0.5rem 0 0;
    font-size: 0.875rem;
    color: var(--text-muted);
  }
`;
const Close = styled.button`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border: 0;
  border-radius: var(--radius-md);
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
  &:hover {
    background: var(--interactive-hover);
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--interactive-focus);
    outline-offset: 2px;
  }
`;
const Content = styled.div`
  padding: 1.5rem;
  @media (max-width: 479px) {
    padding: 1rem;
  }
`;
const Footer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
  @media (max-width: 479px) {
    padding: 1rem;
    > button {
      flex: 1;
    }
  }
`;
export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  role?: string;
}
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      isOpen,
      onClose,
      title,
      description,
      size = 'md',
      closeOnOverlayClick = true,
      closeOnEscape = true,
      showCloseButton = true,
      children,
      footer,
      className,
      'data-testid': testId,
      'aria-labelledby': labelledBy,
      'aria-describedby': describedBy,
      role = 'dialog',
    },
    ref,
  ) => {
    const modalRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const descriptionId = useId();
    useImperativeHandle(ref, () => modalRef.current as HTMLDivElement);
    useEffect(() => {
      if (!isOpen) return;
      const previousFocus = document.activeElement as HTMLElement | null;
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      modalRef.current?.focus();
      return () => {
        document.body.style.overflow = previousOverflow;
        if (previousFocus?.isConnected) previousFocus.focus();
      };
    }, [isOpen]);
    useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (event: KeyboardEvent) => {
        const modal = modalRef.current;
        if (!modal) return;
        if (event.key === 'Escape' && closeOnEscape) {
          event.preventDefault();
          onClose();
        }
        if (event.key !== 'Tab') return;
        const focusable = Array.from(
          modal.querySelectorAll<HTMLElement>(
            'button:not(:disabled), a[href], input:not(:disabled):not([type="hidden"]), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((element) => {
          if (element.tabIndex < 0 || element.closest('[hidden], [inert]'))
            return false;
          let ancestor: HTMLElement | null = element;
          while (ancestor && ancestor !== modal) {
            const style = getComputedStyle(ancestor);
            if (style.display === 'none' || style.visibility === 'hidden')
              return false;
            ancestor = ancestor.parentElement;
          }
          return true;
        });
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first || !last) {
          event.preventDefault();
          modal.focus();
          return;
        }
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === modal ||
            !modal.contains(document.activeElement))
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === modal ||
            !modal.contains(document.activeElement))
        ) {
          event.preventDefault();
          first.focus();
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeOnEscape, onClose]);
    if (!isOpen) return null;
    return createPortal(
      <Overlay
        onClick={(event) => {
          if (closeOnOverlayClick && event.target === event.currentTarget)
            onClose();
        }}
      >
        <Container
          ref={modalRef}
          $size={size}
          className={className}
          role={role}
          aria-modal="true"
          aria-labelledby={labelledBy || (title ? titleId : undefined)}
          aria-describedby={
            describedBy || (description ? descriptionId : undefined)
          }
          tabIndex={-1}
          data-testid={testId || 'modal'}
        >
          {showCloseButton && (
            <Close
              type="button"
              onClick={onClose}
              aria-label="Cerrar modal"
              data-testid="modal-close-button"
            >
              <X size={20} aria-hidden />
            </Close>
          )}
          {(title || description) && (
            <Header>
              {title && <h2 id={titleId}>{title}</h2>}
              {description && <p id={descriptionId}>{description}</p>}
            </Header>
          )}
          <Content>{children}</Content>
          {footer && <Footer>{footer}</Footer>}
        </Container>
      </Overlay>,
      document.body,
    );
  },
);
Modal.displayName = 'Modal';
export default Modal;
